// Regras puras da tela de Conexões (/connections) e da filha
// "Gerenciar conexões" (/allConnections).
// Separadas dos componentes para ficarem testáveis sem montar o React.
import { i18n } from "../../translate/i18n";
import { descreveFalha } from "../../helpers/erroMeta";

// Status gravados em `Whatsapps.status` — contrato do backend.
export const STATUS_CONECTADO = "CONNECTED";
export const STATUS_AGUARDANDO = ["qrcode", "OPENING", "PAIRING"];

/**
 * Uma conexão "precisa de atenção" quando não está conectada.
 *
 * Inclui as que estão aguardando QR: elas não enviam nem recebem, e é
 * justamente isso que o header quer sinalizar.
 */
export const precisaAtencao = (whatsapp) =>
  whatsapp?.status !== STATUS_CONECTADO;

export const aguardandoQr = (whatsapp) =>
  STATUS_AGUARDANDO.includes(whatsapp?.status);

/**
 * Subtítulo do header: "1 conexão precisa de atenção · 38 empresas na
 * plataforma".
 *
 * A parte das empresas só existe para super admin — para os demais o
 * `companies` chega indefinido e a frase termina antes, sem separador solto.
 */
export const headerSubtitle = (whatsapps = [], companies) => {
  const atencao = whatsapps.filter(precisaAtencao).length;

  const partes = [
    atencao === 0
      ? i18n.t("connections.allOperational")
      : i18n.t(
          atencao === 1
            ? "aeros.connections.needsAttentionOne"
            : "aeros.connections.needsAttention",
          { count: atencao }
        ),
  ];

  if (companies || companies === 0) {
    partes.push(
      i18n.t(
        companies === 1
          ? "aeros.connections.companiesOne"
          : "aeros.connections.companies",
        { count: companies }
      )
    );
  }

  return partes.join(" · ");
};

/**
 * Número da conexão. O canal oficial guarda o telefone em `phone_number`;
 * o Baileys, em `number`. Ler só um dos dois deixa metade da lista com "—".
 */
export const numeroBruto = (whatsapp) => {
  if (!whatsapp) return "";
  if (whatsapp.channel === "whatsapp_oficial") return whatsapp.phone_number || "";
  return whatsapp.number || "";
};

/**
 * Rótulo do canal. O banco grava `whatsapp` / `whatsapp_oficial`; mostrar o
 * valor cru na tabela vaza nome de coluna para o usuário.
 */
export const channelLabel = (channel) => {
  const chave = channel === "whatsapp_oficial" ? "official" : "baileys";
  return i18n.t(`aeros.connections.channel.${chave}`);
};

/**
 * Subtítulo do modal de conexão: "WhatsApp (Baileys) · +55 11 94002-8922 ·
 * conexão padrão".
 *
 * Recebe o número já formatado porque a máscara é apresentação; aqui só se
 * decide quais partes existem. Conexão nova (sem canal nem número) devolve
 * string vazia em vez de um "·" solto.
 */
export const modalSubtitle = (whatsapp, numeroFormatado) => {
  if (!whatsapp) return "";
  const partes = [];
  if (whatsapp.channel) partes.push(channelLabel(whatsapp.channel));
  if (numeroFormatado) partes.push(numeroFormatado);
  if (whatsapp.isDefault) partes.push(i18n.t("aeros.connections.defaultOne"));
  return partes.join(" · ");
};

/**
 * Contadores da página "Gerenciar conexões".
 *
 * "Com problema" é o complemento de conectadas, e não uma lista de status de
 * erro: status novo no backend entraria em nenhuma das duas contagens e o
 * total deixaria de fechar.
 */
export const superAdminTotals = (whatsapps = [], companies = []) => {
  const conectadas = whatsapps.filter(
    (w) => w.status === STATUS_CONECTADO
  ).length;
  return {
    companies: companies.length,
    connections: whatsapps.length,
    connected: conectadas,
    problem: whatsapps.length - conectadas,
  };
};

/**
 * Filtra a lista da página filha por texto (empresa ou número) e por status.
 *
 * A busca cobre nome da conexão, número e nome da empresa porque o campo do
 * mockup diz "Empresa ou número…" — e quem procura por empresa costuma
 * digitar o nome da conexão sem perceber a diferença.
 */
export const filtraConexoes = (whatsapps = [], { termo = "", status = "" } = {}) => {
  const t = termo.trim().toLowerCase();
  return whatsapps.filter((w) => {
    if (status && status !== "all") {
      if (status === "connected" && w.status !== STATUS_CONECTADO) return false;
      if (status === "problem" && w.status === STATUS_CONECTADO) return false;
    }
    if (!t) return true;
    return [w.name, numeroBruto(w), w.company?.name]
      .filter(Boolean)
      .some((campo) => String(campo).toLowerCase().includes(t));
  });
};

// Rótulo e tom do status, compartilhados pelas duas telas.
export const statusMeta = (status) => {
  if (status === STATUS_CONECTADO) {
    return { key: "connected", tone: "success" };
  }
  if (STATUS_AGUARDANDO.includes(status)) {
    return { key: "waiting", tone: "warning" };
  }
  return { key: "disconnected", tone: "error" };
};

export const statusLabel = (status) =>
  i18n.t(`aeros.connections.status.${statusMeta(status).key}`);

/**
 * Selo da conexão marcada com problema de conta na Meta (accountIssueCode).
 * Devolve null quando não há marca. `label` é o rótulo curto do chip
 * (metaErrors.badge.<codigo> ou .default); `titulo`/`acao`/`link` vêm do
 * catálogo e servem de tooltip e de link de correção.
 */
export const seloContaMeta = (whatsapp) => {
  const code = whatsapp?.accountIssueCode;
  if (code === null || code === undefined || code === "") return null;
  const d = descreveFalha({
    failureCode: code,
    failureReason: whatsapp.accountIssueReason,
    failureLink: whatsapp.accountIssueLink,
  });
  const chave = `metaErrors.badge.${d.code}`;
  const label = d.code !== null && i18n.exists(chave) ? i18n.t(chave) : i18n.t("metaErrors.badge.default");
  return { code: d.code, label, titulo: d.titulo, acao: d.acao, link: d.link };
};
